/**
 * usePostLoginCleanup — runs once after successful auth to clean up URL params.
 *
 * When a user opens a deep link like `?email=x&otp=y&otpId=z`, Dexie Cloud
 * (or our SignIn component) auto-logs-in using those params. However, the auth
 * params remain in the URL after login. This hook strips them.
 *
 * Also sets a `totodo_auth_just_logged_in` flag in localStorage that
 * can be consumed once by other hooks (e.g., onboarding, push notification deferral).
 *
 * Ported from v1:
 * - Login redirect: reads `totodo_nav_last_url` and navigates to it.
 *   The stored value is path + query string (MOB-08), so this also
 *   reopens the task that was active (`?tid=`) when the app was last
 *   closed. `useStaleTaskDetection.ts` is sync-race-aware so it won't
 *   falsely close a restored task that simply hasn't synced down yet.
 * - Login time measurement: logs duration from app mount to first authenticated render
 * - Permission flag: sets `totodo_check_for_permissions` on native for post-login prompts
 *
 * Runs inside AppLayout (after DatabaseProvider resolves = user is authenticated).
 */

import { useEffect, useRef } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Capacitor } from "@capacitor/core"
import { getLastVisitedUrl, NAV_LAST_URL_KEY } from "./useNavigationHistory"

/** Params to strip from URL after auth completes. */
const AUTH_PARAMS = ["email", "otp", "otpId", "origin", "invited-email"]

/** localStorage key for "just logged in" flag. */
export const JUST_LOGGED_IN_KEY = "totodo_auth_just_logged_in"

/** localStorage key for native permission prompt after login. */
export const CHECK_PERMISSIONS_KEY = "totodo_check_for_permissions"

/** localStorage key for login start time (performance measurement). */
export const LOGIN_START_TIME_KEY = "totodo_login_start_time"

/**
 * Read and clear the "just logged in" flag.
 * Returns true exactly once after login.
 */
export function consumeJustLoggedIn(): boolean {
  const value = localStorage.getItem(JUST_LOGGED_IN_KEY)
  if (value) {
    localStorage.removeItem(JUST_LOGGED_IN_KEY)
    return true
  }
  return false
}

/**
 * Read and clear the permission check flag.
 * Returns true exactly once after native login.
 */
export function consumePermissionCheck(): boolean {
  const value = localStorage.getItem(CHECK_PERMISSIONS_KEY)
  if (value) {
    localStorage.removeItem(CHECK_PERMISSIONS_KEY)
    return true
  }
  return false
}

/**
 * Call this when the login flow starts (e.g., on sign-in page mount)
 * to begin measuring login duration.
 */
export function markLoginStart(): void {
  localStorage.setItem(LOGIN_START_TIME_KEY, String(performance.now()))
}

export function usePostLoginCleanup(): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    // ── Login time measurement ──
    const startTimeStr = localStorage.getItem(LOGIN_START_TIME_KEY)
    if (startTimeStr) {
      const duration = performance.now() - Number(startTimeStr)
      console.log(`[Auth] Login completed in ${Math.round(duration)}ms`)
      localStorage.removeItem(LOGIN_START_TIME_KEY)
    }

    // Check if any auth params are in the URL
    const hasAuthParams = AUTH_PARAMS.some((p) => searchParams.has(p))

    if (hasAuthParams) {
      // Set the "just logged in" flag — other hooks can consume this
      localStorage.setItem(JUST_LOGGED_IN_KEY, "true")

      // NOTE: initialSyncActive is NOT set here. It is ONLY set by
      // DatabaseProvider when InitialSyncLoader becomes visible (new device,
      // no local data). Auth params in URL don't imply a data download is needed.

      // Set permission flag on native — triggers post-login permission prompts
      if (Capacitor.isNativePlatform()) {
        localStorage.setItem(CHECK_PERMISSIONS_KEY, "true")
      }

      // Remove auth params without triggering a full navigation
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          AUTH_PARAMS.forEach((p) => next.delete(p))
          return next
        },
        { replace: true },
      )
    }

    // ── Login redirect: navigate to last visited URL ──
    // Only redirect if we're on the root path (user was signed out → re-auth)
    const currentPath = window.location.pathname
    if (currentPath === "/" || currentPath === "") {
      const lastUrl = getLastVisitedUrl()
      if (lastUrl) {
        // Clear the stored URL so we don't redirect again
        localStorage.removeItem(NAV_LAST_URL_KEY)
        navigate(lastUrl, { replace: true })
      }
    }
  }, [searchParams, setSearchParams, navigate])
}
